/**
 * IdentitySelector
 *
 * Componente riusabile per configurare l'identità di un caller non-user
 * (Personal Assistant, AI Agent, Workflow, API Key, MCP Gateway Key).
 *
 * Espone tre stati:
 *   - tenant-wide  → acts_as_user_ids = []   (richiede admin)
 *   - single-user  → acts_as_user_ids = [u]
 *   - multi-user   → acts_as_user_ids = [u1, u2, ...]
 *
 * Più whitelist mittenti opzionale (allowed_sender_ids = null | string[]).
 *
 * Sender list popolata da /messaging/senders (admin view) e
 * /email-accounts (lista completa account email del tenant).
 */
import { useEffect, useState } from 'react';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { apiClient } from '@/lib/api';
import { Users, User as UserIcon, Globe, X } from 'lucide-react';

export type IdentityMode = 'tenant_wide' | 'single_user' | 'multi_user';

export interface IdentitySelectorValue {
  acts_as_user_ids: string[];
  allowed_sender_ids: string[] | null;
}

interface TenantUser {
  id: string;
  first_name?: string;
  last_name?: string;
  email: string;
}

interface MessagingSender {
  id: string;
  phone_number: string;
  channel_type: string;
  is_default?: boolean;
}

interface EmailAccount {
  id: string;
  email_address: string;
  account_type: string;
}

interface IdentitySelectorProps {
  value: IdentitySelectorValue;
  onChange: (next: IdentitySelectorValue) => void;
  /** true se l'utente corrente è admin/super_admin: abilita modalità tenant-wide. */
  canTenantWide?: boolean;
  /** Mostra/nascondi sezione whitelist mittenti. Default: true. */
  showSenderWhitelist?: boolean;
  className?: string;
}

function deriveMode(actsAs: string[]): IdentityMode {
  if (!actsAs || actsAs.length === 0) return 'tenant_wide';
  if (actsAs.length === 1) return 'single_user';
  return 'multi_user';
}

export default function IdentitySelector({
  value,
  onChange,
  canTenantWide = false,
  showSenderWhitelist = true,
  className,
}: IdentitySelectorProps) {
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [senders, setSenders] = useState<MessagingSender[]>([]);
  const [emailAccounts, setEmailAccounts] = useState<EmailAccount[]>([]);
  const [mode, setMode] = useState<IdentityMode>(deriveMode(value.acts_as_user_ids || []));
  const [whitelistEnabled, setWhitelistEnabled] = useState<boolean>(Array.isArray(value.allowed_sender_ids));

  useEffect(() => {
    setMode(deriveMode(value.acts_as_user_ids || []));
    setWhitelistEnabled(Array.isArray(value.allowed_sender_ids));
  }, [value.acts_as_user_ids, value.allowed_sender_ids]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [usersRes, sendersRes, emailRes] = await Promise.all([
          apiClient.get('/users').catch(() => ({ data: [] })),
          apiClient.get('/messaging/senders?adminView=true').catch(() => ({ data: { senders: [] } })),
          apiClient.get('/email-accounts').catch(() => ({ data: [] })),
        ]);
        if (!alive) return;
        type UsersResp = { data?: { users?: TenantUser[] } | TenantUser[] };
        type SendersResp = { data?: { senders?: MessagingSender[]; data?: { senders?: MessagingSender[] } } };
        type EmailResp = { data?: { accounts?: EmailAccount[] } | EmailAccount[] };
        const u = (usersRes as UsersResp).data;
        const usersData: TenantUser[] = Array.isArray(u) ? u : (u?.users ?? []);
        const sd = (sendersRes as SendersResp).data;
        const sendersData: MessagingSender[] = sd?.senders ?? sd?.data?.senders ?? [];
        const ea = (emailRes as EmailResp).data;
        const emailData: EmailAccount[] = Array.isArray(ea) ? ea : (ea?.accounts ?? []);
        setUsers(Array.isArray(usersData) ? usersData : []);
        setSenders(Array.isArray(sendersData) ? sendersData : []);
        setEmailAccounts(Array.isArray(emailData) ? emailData : []);
      } catch {
        /* noop: i campi restano vuoti, l'utente vedrà liste vuote */
      }
    })();
    return () => { alive = false; };
  }, []);

  const setActsAs = (next: string[]) => {
    onChange({ ...value, acts_as_user_ids: next });
  };

  const setAllowed = (next: string[] | null) => {
    onChange({ ...value, allowed_sender_ids: next });
  };

  const handleModeChange = (m: IdentityMode) => {
    setMode(m);
    if (m === 'tenant_wide') setActsAs([]);
    else if (m === 'single_user') setActsAs(value.acts_as_user_ids?.slice(0, 1) || []);
    else setActsAs(value.acts_as_user_ids || []);
  };

  const addUser = (id: string) => {
    if (!id || value.acts_as_user_ids?.includes(id)) return;
    setActsAs([...(value.acts_as_user_ids || []), id]);
  };

  const removeUser = (id: string) => {
    setActsAs((value.acts_as_user_ids || []).filter((x) => x !== id));
  };

  const userLabel = (id: string): string => {
    const u = users.find((x) => x.id === id);
    if (!u) return id;
    const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
    return name ? `${name} (${u.email})` : u.email;
  };

  const allowedSenderIds = value.allowed_sender_ids || [];
  const toggleAllowedSender = (id: string) => {
    const current = new Set(allowedSenderIds);
    if (current.has(id)) current.delete(id); else current.add(id);
    setAllowed(Array.from(current));
  };
  const toggleAllowedEmail = (id: string) => {
    const current = new Set(allowedSenderIds);
    if (current.has(id)) current.delete(id); else current.add(id);
    setAllowed(Array.from(current));
  };

  return (
    <div className={`space-y-4 ${className || ''}`}>
      <div className="space-y-2">
        <Label className="text-sm font-medium">Identità del caller</Label>
        <p className="text-xs text-muted-foreground">
          Definisce per conto di quali utenti questo caller può inviare messaggi/email
          e quali numeri/account vede.
        </p>
        <div className="flex gap-2">
          <Button
            type="button"
            variant={mode === 'tenant_wide' ? 'default' : 'outline'}
            size="sm"
            disabled={!canTenantWide}
            onClick={() => handleModeChange('tenant_wide')}
            title={canTenantWide ? 'Vede tutti i mittenti globali del tenant' : 'Riservato agli admin'}
          >
            <Globe className="h-3.5 w-3.5 mr-1" />
            Tenant-wide
          </Button>
          <Button
            type="button"
            variant={mode === 'single_user' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleModeChange('single_user')}
          >
            <UserIcon className="h-3.5 w-3.5 mr-1" />
            Singolo utente
          </Button>
          <Button
            type="button"
            variant={mode === 'multi_user' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleModeChange('multi_user')}
          >
            <Users className="h-3.5 w-3.5 mr-1" />
            Multi-utente
          </Button>
        </div>
      </div>

      {mode === 'single_user' && (
        <div className="space-y-2">
          <Label>Utente</Label>
          <Select
            value={(value.acts_as_user_ids || [])[0] || ''}
            onValueChange={(v) => setActsAs(v ? [v] : [])}
          >
            <SelectTrigger><SelectValue placeholder="Seleziona utente..." /></SelectTrigger>
            <SelectContent>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>{userLabel(u.id)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {mode === 'multi_user' && (
        <div className="space-y-2">
          <Label>Utenti rappresentati</Label>
          <Select value="" onValueChange={addUser}>
            <SelectTrigger><SelectValue placeholder="Aggiungi utente..." /></SelectTrigger>
            <SelectContent>
              {users
                .filter((u) => !(value.acts_as_user_ids || []).includes(u.id))
                .map((u) => (
                  <SelectItem key={u.id} value={u.id}>{userLabel(u.id)}</SelectItem>
                ))}
            </SelectContent>
          </Select>
          <div className="flex flex-wrap gap-2 mt-2">
            {(value.acts_as_user_ids || []).map((id) => (
              <Badge key={id} variant="secondary" className="gap-1">
                {userLabel(id)}
                <button
                  type="button"
                  onClick={() => removeUser(id)}
                  className="ml-1 hover:text-red-600"
                  aria-label="Rimuovi"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {(value.acts_as_user_ids || []).length === 0 && (
              <p className="text-xs text-muted-foreground">Nessun utente selezionato.</p>
            )}
          </div>
        </div>
      )}

      {showSenderWhitelist && (
        <div className="space-y-2 border-t pt-3">
          <div className="flex items-center justify-between">
            <div>
              <Label>Whitelist mittenti</Label>
              <p className="text-xs text-muted-foreground">
                Restringe ulteriormente i mittenti utilizzabili rispetto a quelli derivati
                dagli utenti (intersezione).
              </p>
            </div>
            <Switch
              checked={whitelistEnabled}
              onCheckedChange={(checked) => {
                setWhitelistEnabled(checked);
                setAllowed(checked ? (value.allowed_sender_ids || []) : null);
              }}
            />
          </div>
          {whitelistEnabled && (
            <div className="space-y-3 mt-2">
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Numeri SMS / WhatsApp</p>
                <div className="flex flex-wrap gap-2">
                  {senders.map((s) => {
                    const checked = allowedSenderIds.includes(s.id);
                    return (
                      <Badge
                        key={s.id}
                        variant={checked ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => toggleAllowedSender(s.id)}
                      >
                        [{s.channel_type}] {s.phone_number}
                      </Badge>
                    );
                  })}
                  {senders.length === 0 && <p className="text-xs text-muted-foreground">Nessun mittente messaging.</p>}
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Account email</p>
                <div className="flex flex-wrap gap-2">
                  {emailAccounts.map((a) => {
                    const checked = allowedSenderIds.includes(a.id);
                    return (
                      <Badge
                        key={a.id}
                        variant={checked ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => toggleAllowedEmail(a.id)}
                      >
                        [{a.account_type}] {a.email_address}
                      </Badge>
                    );
                  })}
                  {emailAccounts.length === 0 && <p className="text-xs text-muted-foreground">Nessun account email.</p>}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
